import React, { useEffect, useMemo, useState } from 'react';
import customerService from '../../services/customerService';
import EscalationMatrix from '../../components/common/EscalationMatrix';
import Toast from '../../components/common/Toast';
import Button from '../../components/common/Button';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import { useSite } from '../../contexts/SiteContext';

const EscalationMatrixView = () => {
  const { user } = useAuth();
  const { selectedSite, sites } = useSite();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const { toasts, removeToast, success, error } = useToast();

  const selectedSiteName = useMemo(() => {
    if (!selectedSite) return '';
    const site = sites.find((item) => item.id === selectedSite || String(item.id) === String(selectedSite));
    return site?.site_name || '';
  }, [selectedSite, sites]);

  useEffect(() => {
    if (selectedSite) {
      loadEscalationMatrix();
    }
  }, [selectedSite, user?.id]);

  const loadEscalationMatrix = async () => {
    setLoading(true);
    const result = await customerService.getEscalationMatrix(user.id, selectedSite);
    if (result.success) {
      setContacts(Array.isArray(result.data) ? result.data : []);
    } else {
      setContacts([]);
      error(result.error || 'Failed to load escalation matrix');
    }
    setLoading(false);
  };

  const handleDownload = async () => {
    if (!selectedSite) {
      error('Please select a site to download its escalation matrix');
      return;
    }
    setDownloading(true);
    const result = await customerService.downloadEscalationMatrix(user.id, selectedSite, selectedSiteName);
    if (result.success) {
      success('Escalation matrix downloaded successfully!');
    } else {
      error(result.error || 'Failed to download escalation matrix');
    }
    setDownloading(false);
  };

  const containerStyle = {
    maxWidth: '1100px',
    margin: '0 auto'
  };

  const loadingStyle = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '14px',
    padding: '28px',
    textAlign: 'center',
    color: 'var(--text-secondary)'
  };

  const headerContainerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    padding: '16px 24px',
    flexWrap: 'wrap',
    gap: '12px'
  };

  const headerTextStyle = {
    flex: 1,
    minWidth: '250px'
  };

  const titleStyle = {
    fontSize: '20px',
    fontWeight: '700',
    color: 'var(--text-primary)',
    margin: '0 0 4px 0'
  };

  const subtitleStyle = {
    fontSize: '13px',
    color: 'var(--text-secondary)',
    margin: 0
  };

  return (
    <div style={containerStyle}>
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}

      <div style={headerContainerStyle}>
        <div style={headerTextStyle}>
          <h2 style={titleStyle}>Escalation Directory</h2>
          <p style={subtitleStyle}>
            {selectedSiteName ? `View and download the escalation matrix for ${selectedSiteName}.` : 'Select a site to view escalation matrix.'}
          </p>
        </div>
        <Button 
          variant="primary" 
          onClick={handleDownload} 
          disabled={downloading || !selectedSite || contacts.length === 0}
        >
          {downloading ? 'Downloading...' : 'Download PDF'}
        </Button>
      </div>

      {loading ? (
        <div style={loadingStyle}>Loading escalation contacts...</div>
      ) : contacts.length === 0 ? (
        <div style={loadingStyle}>No escalation matrix configured for this site.</div>
      ) : (
        <EscalationMatrix
          contacts={contacts}
          title="Escalation Matrix"
          description={selectedSiteName ? `Escalation path for ${selectedSiteName}.` : 'Escalation path for the selected site.'}
        />
      )}
    </div>
  );
};

export default EscalationMatrixView;
